import {
  DeesElement,
  customElement,
  property,
  state,
  html,
  css,
  cssManager,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { zIndexLayers } from '../../00zindex.js';
import type { IAppLocation } from '../../interfaces/applocation.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import { DeesContextmenu } from '../../00group-overlay/dees-contextmenu/dees-contextmenu.js';
import { demoFunc } from '../dees-appui/dees-appui.demo.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-appui-location': DeesAppuiLocation;
  }
}

/** A controlled app address field. URL policy and routing belong to its host. */
@customElement('dees-appui-location')
export class DeesAppuiLocation extends DeesElement {
  public static demo = () => demoFunc();
  public static demoGroups = ['App UI'];

  @property({ attribute: false })
  public accessor location: IAppLocation | undefined;

  @state()
  private accessor editing = false;

  @state()
  private accessor draft = '';

  @state()
  private accessor pending = false;

  @state()
  private accessor message = '';

  @state()
  private accessor failed = false;

  @state()
  private accessor copied = false;

  private feedbackTimer?: ReturnType<typeof setTimeout>;
  private generation = 0;
  private ancestorMenu?: DeesContextmenu;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
        position: relative;
        min-width: 0;
        width: 100%;
        max-width: 560px;
        container-type: inline-size;
        -webkit-app-region: no-drag;
        font: var(--dees-font-control-size)/1.3 var(--dees-font-family);
        color: var(--dees-color-text-secondary);
      }

      .field {
        display: flex;
        align-items: center;
        height: 28px;
        min-width: 0;
        border: 1px solid var(--dees-color-border-subtle);
        border-radius: var(--dees-radius-md);
        background: var(--dees-color-fill-secondary);
      }

      .field:focus-within {
        border-color: var(--dees-color-focus-ring);
        box-shadow: 0 0 0 1px var(--dees-color-focus-ring);
      }

      :host([editing]) .field {
        background: var(--dees-color-bg-secondary);
      }

      .field.failed {
        border-color: var(--dees-color-text-error);
      }

      .field.failed:focus-within {
        box-shadow: 0 0 0 1px var(--dees-color-text-error);
      }

      nav {
        display: flex;
        align-items: center;
        overflow: hidden;
        min-width: 0;
        padding-left: 2px;
      }

      button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        font: inherit;
        color: inherit;
        border: 0;
        background: transparent;
        cursor: pointer;
        border-radius: 4px;
        height: 24px;
        padding: 0 6px;
      }

      button:hover {
        color: var(--dees-color-text-primary);
        background: var(--dees-color-hover);
      }

      button:focus-visible {
        outline: 2px solid var(--dees-color-focus-ring);
        outline-offset: -2px;
      }

      button:disabled {
        opacity: .5;
        cursor: default;
      }

      .crumb {
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 160px;
        min-width: 28px;
        flex-shrink: 1;
        white-space: nowrap;
      }

      .current {
        font-weight: 600;
        color: var(--dees-color-text-primary);
        white-space: nowrap;
        padding: 0 6px;
        overflow: hidden;
        text-overflow: ellipsis;
        flex-shrink: 0;
        max-width: 230px;
      }

      .separator {
        flex: none;
        color: var(--dees-color-text-muted);
        padding: 0 2px;
      }

      .edit-location {
        flex: 1;
        min-width: 24px;
        align-self: stretch;
        height: auto;
        cursor: text;
        justify-content: end;
      }

      .edit-location:hover {
        background: transparent;
      }

      .edit-location dees-icon {
        opacity: 0;
      }

      .field:hover .edit-location dees-icon,
      .edit-location:focus-visible dees-icon {
        opacity: .65;
      }

      .ancestors {
        display: none;
      }

      .copy {
        width: 28px;
        margin-right: 1px;
      }

      input {
        width: 0;
        min-width: 0;
        flex: 1;
        height: 100%;
        padding: 0 9px;
        border: 0;
        outline: none;
        box-shadow: none;
        color: var(--dees-color-text-primary);
        background: transparent;
        font: inherit;
        user-select: text;
        border-radius: inherit;
      }

      .message {
        position: absolute;
        inset: calc(100% + 6px) 0 auto;
        z-index: ${zIndexLayers.overlay.dropdown};
        padding: 8px 10px;
        font-size: 12px;
        line-height: 1.4;
        white-space: normal;
        overflow-wrap: anywhere;
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-md);
        background: var(--dees-color-bg-primary);
        box-shadow: var(--dees-shadow-md);
        color: var(--dees-color-text-secondary);
      }

      @container (max-width: 300px) {
        .crumb,
        .separator {
          display: none;
        }

        .ancestors {
          display: inline-flex;
        }

        .current {
          flex-shrink: 1;
          min-width: 0;
        }
      }

      @container (max-width: 135px) {
        nav {
          display: none;
        }

        .edit-location dees-icon {
          opacity: .65;
        }

        .edit-location {
          justify-content: center;
        }
      }

      @media (pointer: coarse) {
        .field {
          height: 40px;
        }

        button {
          height: 38px;
          min-width: 38px;
        }

        .copy {
          width: 38px;
        }
      }
    `,
  ];

  public render() {
    if (!this.location) return html``;
    return html`<div class="field ${this.failed ? 'failed' : ''}" aria-busy=${this.pending}>
      ${this.editing
        ? html`<input
            aria-label="App location"
            aria-invalid=${this.failed}
            aria-describedby=${this.message ? 'location-message' : ''}
            spellcheck="false"
            autocomplete="off"
            autocapitalize="off"
            .value=${this.draft}
            ?readonly=${this.pending}
            @input=${(event: InputEvent) => {
              this.draft = (event.target as HTMLInputElement).value;
              this.clearFeedback();
            }}
            @keydown=${(event: KeyboardEvent) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void this.submit();
              } else if (event.key === 'Escape') {
                event.preventDefault();
                this.cancel();
              }
            }}
            @blur=${() => {
              if (!this.pending && this.draft === this.location?.href) {
                this.editing = false;
                this.clearFeedback();
              }
            }}
          >`
        : html`<nav aria-label="Current location">
              ${this.location.items.some(item => item.href)
                ? html`<button
                    class="ancestors"
                    type="button"
                    aria-label="Parent screens"
                    aria-haspopup="menu"
                    ?disabled=${this.pending}
                    @click=${this.showAncestors}
                  >…</button>`
                : ''}
              ${this.location.items.map((item, index) => html`
                ${index ? html`<span class="separator" aria-hidden="true">/</span>` : ''}
                ${item.href
                  ? html`<button
                      class="crumb"
                      type="button"
                      title=${item.label}
                      ?disabled=${this.pending}
                      @click=${() => this.open(item.href!)}
                    >${item.label}</button>`
                  : html`<span class="current" aria-current="page" title=${item.label}>${item.label}</span>`}
              `)}
            </nav>
            <button
              class="edit-location"
              type="button"
              aria-label="Edit app location"
              title="Edit or paste an app link"
              ?disabled=${this.pending}
              @click=${this.edit}
              @keydown=${(event: KeyboardEvent) => {
                if (event.key === 'F2') {
                  event.preventDefault();
                  void this.edit();
                }
              }}
            ><dees-icon icon="lucide:Pencil" iconSize="12"></dees-icon></button>`}
      <button
        class="copy"
        type="button"
        aria-label=${this.copied ? 'Link copied' : 'Copy link to this screen'}
        title=${this.copied ? 'Copied' : 'Copy link'}
        ?disabled=${this.pending}
        @click=${this.copy}
      ><dees-icon .icon=${this.copied ? 'lucide:Check' : 'lucide:Link'} iconSize="14"></dees-icon></button>
    </div>${this.message
      ? html`<div class="message" id="location-message" role="status">${this.message}</div>`
      : ''}`;
  }

  public async edit() {
    if (!this.location || this.pending) return;
    this.clearFeedback();
    this.draft = this.location.href;
    this.editing = true;
    await this.updateComplete;
    const input = this.shadowRoot?.querySelector('input');
    input?.focus();
    input?.select();
  }

  private async showAncestors(event: MouseEvent) {
    await this.ancestorMenu?.destroy();
    const generation = this.generation;
    const trigger = event.currentTarget as HTMLButtonElement;
    trigger.focus();
    const rect = trigger.getBoundingClientRect();
    const menu = await DeesContextmenu.openContextMenuWithOptions(
      new MouseEvent('contextmenu', { clientX: rect.left, clientY: rect.bottom }),
      (this.location?.items || [])
        .filter(item => item.href)
        .map(item => ({
          name: item.label,
          action: async () => {
            await this.open(item.href!);
          },
        })),
    );
    if (!this.isConnected || generation !== this.generation) {
      await menu?.destroy();
      return;
    }
    this.ancestorMenu = menu;
  }

  private cancel() {
    if (this.pending) return;
    this.editing = false;
    this.clearFeedback();
    void this.updateComplete.then(() =>
      this.shadowRoot?.querySelector<HTMLButtonElement>('.edit-location')?.focus(),
    );
  }

  private async submit() {
    if (this.draft.trim() === this.location?.href) {
      this.cancel();
      return;
    }
    await this.open(this.draft.trim());
  }

  private async open(href: string) {
    if (!this.location || this.pending) return;
    const generation = ++this.generation;
    this.pending = true;
    this.clearFeedback();
    try {
      const navigated = await this.location.navigate(href);
      if (generation !== this.generation || !this.isConnected) return;
      if (!navigated) throw new Error('Navigation was stopped. Save or discard your changes before leaving.');
      this.editing = false;
      await this.updateComplete;
      this.shadowRoot?.querySelector<HTMLButtonElement>('.edit-location')?.focus();
    } catch (error) {
      if (generation !== this.generation || !this.isConnected) return;
      this.failed = true;
      this.message = error instanceof Error ? error.message : 'Could not open this app link.';
    } finally {
      if (generation === this.generation) this.pending = false;
    }
  }

  private async copy() {
    if (!this.location) return;
    const generation = ++this.generation;
    this.clearFeedback();
    try {
      await navigator.clipboard.writeText(this.location.href);
      if (!this.isConnected || generation !== this.generation) return;
      this.copied = true;
      this.message = 'Link copied';
      this.feedbackTimer = setTimeout(() => this.clearFeedback(), 1800);
    } catch {
      if (!this.isConnected || generation !== this.generation) return;
      await this.edit();
      this.message = 'Clipboard access is unavailable. Copy the selected link.';
    }
  }

  private clearFeedback() {
    clearTimeout(this.feedbackTimer);
    this.message = '';
    this.failed = false;
    this.copied = false;
  }

  public updated(changes: Map<string, unknown>) {
    super.updated(changes);
    this.toggleAttribute('editing', this.editing);
    const previous = changes.get('location') as IAppLocation | undefined;
    if (previous && previous.href !== this.location?.href) {
      this.editing = false;
      this.clearFeedback();
    }
  }

  public async disconnectedCallback() {
    this.generation++;
    this.pending = false;
    this.clearFeedback();
    void this.ancestorMenu?.destroy();
    await super.disconnectedCallback();
  }
}
